import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface AssessmentStatus {
    activeRun: { id: string; name: string } | null;
    latestAttempt: {
        id: string;
        completed: boolean;
        started_at: string;
        completed_at: string | null;
        responseCount: number;
    } | null;
    loading: boolean;
    refreshStatus: () => Promise<void>;
}

export const useAssessmentStatus = (): AssessmentStatus => {
    const { user } = useAuth();
    const [activeRun, setActiveRun] = useState<AssessmentStatus['activeRun']>(null);
    const [latestAttempt, setLatestAttempt] = useState<AssessmentStatus['latestAttempt']>(null);
    const [loading, setLoading] = useState(true);

    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            // 1. Fetch active run
            const { data: run } = await supabase
                .from('assessment_runs')
                .select('id, name')
                .eq('is_active', true)
                .maybeSingle();

            setActiveRun(run);

            if (run && user) {
                // 2. Fetch latest attempt for this run
                const { data: attempt } = await supabase
                    .from('attempts')
                    .select('id, completed, started_at, completed_at')
                    .eq('user_id', user.id)
                    .eq('run_id', run.id)
                    .order('started_at', { ascending: false })
                    .maybeSingle();

                if (attempt) {
                    // 3. Fetch response count
                    const { count } = await supabase
                        .from('responses')
                        .select('*', { count: 'exact', head: true })
                        .eq('attempt_id', attempt.id);

                    setLatestAttempt({ ...attempt, responseCount: count || 0 });
                } else {
                    setLatestAttempt(null);
                }
            } else {
                setLatestAttempt(null);
            }
        } catch (error) {
            console.error('Error fetching assessment status:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        activeRun,
        latestAttempt,
        loading,
        refreshStatus: fetchStatus
    };
};
